import type { TourAction, TourActionContext } from '@/tours/types';
import { logger } from '@/utils/logger';
import { waitForCondition, waitForSelector, waitForTourElement } from './waitForElement';

/**
 * Executors for scripted tour playback. Every executor performs REAL
 * interactions — native input/click events that flow through the app's own
 * handlers — so the state a demo produces is genuine and survives exiting
 * the tour. All executors honor cancellation between atomic operations and
 * degrade to instant (no animation) under prefers-reduced-motion.
 */

// Pacing tuned for a human watching, not a test runner: the spotlight
// settles before anything happens, typing has a natural cadence, and each
// action's result gets a beat to register before the next one starts.
const CLICK_PULSE_MS = 400;
const TYPE_CHAR_MIN_MS = 45;
const TYPE_CHAR_JITTER_MS = 35;
const HIGHLIGHT_SETTLE_MS = 550;
const BETWEEN_ACTIONS_MS = 550;

export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Restartable attention pulse on the element being acted on. */
export function pulse(el: HTMLElement): void {
  el.classList.remove('tour-action-pulse');
  // Force a reflow so re-adding the class restarts the animation.
  void el.offsetWidth;
  el.classList.add('tour-action-pulse');
  setTimeout(() => {
    el.classList.remove('tour-action-pulse');
  }, 900);
}

/**
 * Dispatch the full mousedown → mouseup → click sequence. Some widgets
 * (Naive select options, table rows) react to mousedown rather than click,
 * and `.click()` alone doesn't produce those.
 */
export function clickThrough(el: HTMLElement): void {
  const base = { bubbles: true, cancelable: true, view: window };
  el.dispatchEvent(new MouseEvent('mousedown', base));
  el.dispatchEvent(new MouseEvent('mouseup', base));
  el.dispatchEvent(new MouseEvent('click', base));
}

export async function clickAnimated(el: HTMLElement, ctx: TourActionContext): Promise<void> {
  pulse(el);
  if (ctx.animate) {
    await delay(CLICK_PULSE_MS);
  }
  if (ctx.isCancelled()) {
    return;
  }
  clickThrough(el);
}

/**
 * Typewriter into an input/textarea (the element itself or the first one
 * inside it — `data-tour` usually sits on the Naive wrapper). Per-character
 * native `input` events keep the app's v-model in sync.
 */
export async function typeInto(
  el: HTMLElement,
  text: string,
  ctx: TourActionContext,
  options?: { charMs?: number }
): Promise<void> {
  const input = el.matches('input, textarea')
    ? (el as HTMLInputElement | HTMLTextAreaElement)
    : el.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  if (!input) {
    logger.warn('[tour] type action found no input inside target', el);
    return;
  }
  const baseMs = options?.charMs ?? TYPE_CHAR_MIN_MS;
  input.focus();
  input.value = '';
  input.dispatchEvent(new Event('input', { bubbles: true }));
  for (const char of text) {
    if (ctx.isCancelled()) {
      return;
    }
    input.value += char;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (ctx.animate) {
      await delay(baseMs + Math.random() * TYPE_CHAR_JITTER_MS);
    }
  }
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Toggle a checkbox-like element unless it's already in the wanted state. */
export async function setChecked(
  el: HTMLElement,
  value: boolean,
  ctx: TourActionContext
): Promise<void> {
  const checkbox = el.matches('.n-checkbox, .n-switch')
    ? el
    : (el.querySelector<HTMLElement>('.n-checkbox, .n-switch') ?? el);
  const isChecked =
    checkbox.classList.contains('n-checkbox--checked') ||
    checkbox.classList.contains('n-switch--active');
  if (isChecked === value) {
    pulse(checkbox);
    return;
  }
  await clickAnimated(checkbox, ctx);
}

/**
 * Drive a Naive select: open the real dropdown, wait for the teleported
 * menu, click the option whose text contains `optionText`.
 */
export async function selectOption(
  el: HTMLElement,
  optionText: string,
  ctx: TourActionContext
): Promise<void> {
  const trigger = el.querySelector<HTMLElement>('.n-base-selection') ?? el;
  await clickAnimated(trigger, ctx);
  if (ctx.isCancelled()) {
    return;
  }
  const menu = await waitForSelector('.n-base-select-menu', 2500);
  if (!menu) {
    logger.warn(`[tour] select menu never opened for option "${optionText}"`);
    return;
  }
  const option = await waitForCondition(() => {
    const options = menu.querySelectorAll<HTMLElement>('.n-base-select-option');
    return [...options].find(o => o.textContent.includes(optionText)) ?? null;
  }, 3000);
  if (!option || ctx.isCancelled()) {
    logger.warn(`[tour] option "${optionText}" not found in select menu — closing it`);
    // Never leave the dropdown hanging open behind the scene.
    clickThrough(trigger);
    return;
  }
  await clickAnimated(option, ctx);
}

/**
 * Drive a Naive select to whatever it offers FIRST. Some options depend on the
 * organisation's own setup — the signing providers are GAIA's, the user's own
 * Youtrust account, or both — so a demo can't name the option it wants and
 * takes what is there instead.
 */
export async function selectFirstOption(el: HTMLElement, ctx: TourActionContext): Promise<void> {
  const trigger = el.querySelector<HTMLElement>('.n-base-selection') ?? el;
  await clickAnimated(trigger, ctx);
  if (ctx.isCancelled()) {
    return;
  }
  const option = await waitForSelector('.n-base-select-menu .n-base-select-option', 3000);
  if (!option || ctx.isCancelled()) {
    logger.warn('[tour] select offered no options — closing it');
    // Never leave the dropdown hanging open behind the scene.
    clickThrough(trigger);
    return;
  }
  await clickAnimated(option, ctx);
}

/**
 * The signal to stop waiting for rows that will never come.
 *
 * Two table implementations to recognise. An empty NDataTable renders no tbody
 * at all — its settled "No Data" placeholder is the tell (the `--hide` modifier
 * is on while loading, so it has to be excluded). GaiaDataTable has no empty
 * state of its own: the caller slots a `GaiaEmptyState` in place of the rows,
 * so its root class is what settles the question there.
 */
export function tableSettledEmpty(container: HTMLElement): boolean {
  return (
    container.querySelector('.n-data-table-empty:not(.n-data-table-empty--hide)') !== null ||
    container.querySelector('.gaia-empty-state') !== null
  );
}

/**
 * Click the first row inside a table anchored by `containerKey` — the
 * standard "open a demo record" move (row clicks that navigate are safe).
 * Resolves true only when a row was actually clicked, so callers can gate
 * the scenes that assume the record is open.
 */
export async function clickFirstRowIn(
  ctx: TourActionContext,
  containerKey: string,
  rowSelector = 'tbody tr'
): Promise<boolean> {
  const container = await waitForTourElement(containerKey);
  if (!container || ctx.isCancelled()) {
    return false;
  }
  const row = await waitForCondition(
    () =>
      container.querySelector<HTMLElement>(rowSelector) ??
      (tableSettledEmpty(container) ? 'empty' : null),
    3000
  );
  if (!row || row === 'empty' || ctx.isCancelled()) {
    logger.warn(`[tour] no rows found in "${containerKey}" to open`);
    return false;
  }
  row.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(row);
  if (ctx.animate) {
    await delay(700);
  }
  await clickAnimated(row, ctx);
  return !ctx.isCancelled();
}

/** Run one scene's actions sequentially; stops silently on cancellation. */
export async function runTourActions(actions: TourAction[], ctx: TourActionContext): Promise<void> {
  try {
    for (const action of actions) {
      if (ctx.isCancelled()) {
        return;
      }
      await runAction(action, ctx);
      if (ctx.animate && !ctx.isCancelled()) {
        await delay(BETWEEN_ACTIONS_MS);
      }
    }
  } finally {
    ctx.setHighlight(null);
  }
}

async function runAction(action: TourAction, ctx: TourActionContext): Promise<void> {
  if (action.kind === 'wait') {
    await delay(action.ms);
    return;
  }
  if (action.kind === 'custom') {
    await action.run(ctx);
    return;
  }

  const el = await waitForTourElement(action.target);
  if (!el) {
    logger.warn(`[tour] action target "${action.target}" not found — skipping action`);
    return;
  }
  if (ctx.isCancelled()) {
    return;
  }
  el.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(el);
  if (ctx.animate) {
    // Let the spotlight glide onto the control before acting on it.
    await delay(HIGHLIGHT_SETTLE_MS);
  }
  if (ctx.isCancelled()) {
    return;
  }

  switch (action.kind) {
    case 'click':
      await clickAnimated(el, ctx);
      break;
    case 'type':
      await typeInto(el, action.text, ctx, { charMs: action.charMs });
      break;
    case 'check':
      await setChecked(el, action.value ?? true, ctx);
      break;
    case 'select':
      await selectOption(el, action.option, ctx);
      break;
  }
}
